/**
 * A pot that exists, and the link you can put on it.
 *
 * The page at `/prepare/:id`, reached three ways: from the fridge on Prepare,
 * from a stored tile's *name* in the add sheet (its body is the common act —
 * eat one — and writes immediately; the name is the uncommon one), and from
 * the flow the moment a pot is stored. It is where the four things you can do
 * to a pot that is already in the fridge live.
 *
 * A page is bookmarkable where a sheet mode was not, so a pot that has been
 * thrown out still answers here — as what happened to it, with nothing left
 * to eat or bin.
 *
 * ## Eating an uneven helping
 *
 * The tile in the sheet eats one portion in one tap, because that is the
 * common act. This is where the uncommon one lives: ¾ of a portion, one and a
 * half, or — for a pot that was weighed — the grams on the plate. The picker
 * says how much and the button says where; the write is the host's, through
 * the same queue as the tile's, with the fridge charged what `helpingFor`
 * worked out.
 *
 * ## Sharing
 *
 * A share is a read of one batch at one token, and two switches decide its
 * content, because the two things worth sharing are not the same thing: the
 * recipe (what is in it, what a portion comes to) and the pot (cooked Sunday,
 * two left, eat by Wednesday). Somebody sending a recipe to a friend wants the
 * first. Somebody telling a flatmate what is stored wants both. Somebody who
 * does not want to hand over their cooking wants only the numbers, and both
 * switches off is a legitimate share — a name and a per-portion figure, which
 * is all "how much is a bowl of that" needs.
 *
 * The switches are live: flipping one changes what the person already holding
 * the link sees. Anything else would mean the page being previewed is not the
 * page that was shared.
 *
 * **The link is made on request, not on arrival.** The composer opens on the
 * switches with no URL until "Make the link" is pressed, because publishing a
 * publicly readable address is an outward-facing act and opening a screen is
 * not a request to take one. Everything above it can be set first, so the link
 * that gets made is the one that was configured.
 *
 * **Revoked links stay in the list.** The useful question weeks later is "did I
 * already share this and did I kill it", and a row that disappeared cannot
 * answer it.
 */

import {
  formatPortions,
  helpingFor,
  ONE_PORTION,
  useBatch,
  useDiscardBatch,
  useRevokeShare,
  useCreateShare,
  useUpdateBatch,
  useUpdateShare,
  type BatchShare,
  type Helping,
  type Meal,
} from "@calorio/core";
import { useState } from "react";

import { HelpingPicker } from "./HelpingPicker";
import { useCalorio } from "../app/providers";

const IN_MEAL: Record<Meal, string> = {
  breakfast: "breakfast",
  lunch: "lunch",
  dinner: "dinner",
  snack: "snacks",
};

const day = (date: string, opts?: Intl.DateTimeFormatOptions) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(
    undefined,
    opts ?? { weekday: "short", day: "numeric", month: "short" },
  );

/** The address a link actually is, on whatever host this app is served from. */
export const shareUrl = (token: string) => `${globalThis.location?.origin ?? ""}/m/${token}`;

/* ========================================================================== *
 * 7c — the pot, once it exists
 * ========================================================================== */

export function PotDetail({
  batchId,
  meal,
  onEat,
  onShare,
  onClose,
}: {
  batchId: string;
  meal: Meal;
  /** Eat this much, through the same queued write the tile itself uses. */
  onEat: (helping: Helping) => void;
  onShare: () => void;
  onClose: () => void;
}) {
  const ctx = useCalorio();
  const batch = useBatch(ctx, batchId);
  const discard = useDiscardBatch(ctx);
  const update = useUpdateBatch(ctx);
  const [counting, setCounting] = useState(false);
  const [helping, setHelping] = useState<Helping>(ONE_PORTION);
  const [padUp, setPadUp] = useState(false);

  if (!batch.data) {
    return (
      <div className="sheet__scroll pot">
        <p className="muted">{batch.isLoading ? "…" : "That pot is not here any more."}</p>
      </div>
    );
  }

  const pot = batch.data;
  // Kept as a number for the guards below; printed as a fraction, because a
  // pot three quarters eaten from has 3¼ left and "3.3" is not a count.
  const left = pot.portions_remaining;
  const live = pot.live_shares;
  const gone = pot.discarded_at !== null;
  const helped = helpingFor(pot, helping);

  const leftLine = [
    `cooked ${day(pot.cooked_date)}`,
    pot.weighed && pot.portion_grams ? `${Math.round(pot.portion_grams)} g each` : null,
    pot.best_before ? `best before ${day(pot.best_before)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="sheet__scroll pot">
      <div className="pot__left">
        <span className="pot__left-count num">{formatPortions(left)}</span>
        <span className="pot__left-of">of {pot.portions_made} portions left</span>
      </div>
      <p className="muted pot__note">
        {gone ? `thrown out ${day(pot.discarded_at!.slice(0, 10))} · ` : ""}
        {leftLine}
      </p>

      <div className="pot__portion">
        <span className="pot__portion-head">One portion</span>
        <span className="pot__portion-unit num">
          {pot.weighed && pot.portion_grams
            ? `${Math.round(pot.portion_grams)} g`
            : `1 of ${pot.portions_made}`}
        </span>
        <span className="pot__portion-kcal num">
          {Math.round(pot.kcal_per_portion ?? 0)} <small>kcal</small>
        </span>
        <span className="pot__portion-macros num">
          P {Math.round(pot.protein_g_per_portion ?? 0)} · C{" "}
          {Math.round(pot.carbs_g_per_portion ?? 0)} · F {Math.round(pot.fat_g_per_portion ?? 0)}
          {pot.kcal_per_100_cooked ? ` · ${Math.round(pot.kcal_per_100_cooked)} kcal / 100 g` : ""}
        </span>
      </div>

      {!gone && (
        <div className="pot__section">
          <h3 className="sheet__heading">How much</h3>
        </div>
      )}
      {!gone && (
        <HelpingPicker
          source={pot}
          name={pot.recipe_name}
          value={helping}
          onChange={setHelping}
          onPad={setPadUp}
        />
      )}

      {!padUp && (
        <ul className="pot__actions">
          <li>
            <button
              className="pot__action"
              onClick={() => onEat(helping)}
              disabled={gone || left <= 0 || helped === null}
            >
              <span>{gone ? "Eat one now" : `Eat ${helped?.label ?? "one"} now`}</span>
              <span className="muted">
                {helped && !gone ? `${Math.round(helped.kcal)} kcal · ` : ""}to {IN_MEAL[meal]}
              </span>
            </button>
          </li>
          <li>
            <button className="pot__action" onClick={onShare}>
              <span>Share this pot</span>
              <span className="muted">
                {live === 0 ? "no link yet" : `${live} link${live === 1 ? "" : "s"} live`}
              </span>
            </button>
          </li>
          <li>
            {counting ? (
              <div className="pot__portions">
                <button
                  className="pot__step"
                  aria-label="One portion fewer"
                  onClick={() =>
                    update.mutate({
                      id: pot.id,
                      portions_made: Math.max(1, pot.portions_made - 1),
                    })
                  }
                >
                  −
                </button>
                <span className="pot__portion-count">
                  <span className="num">{pot.portions_made}</span>
                  <span className="pot__portion-label">portions out of this pot</span>
                </span>
                <button
                  className="pot__step"
                  aria-label="One portion more"
                  onClick={() =>
                    update.mutate({
                      id: pot.id,
                      portions_made: pot.portions_made + 1,
                    })
                  }
                >
                  +
                </button>
              </div>
            ) : (
              <button className="pot__action" onClick={() => setCounting(true)}>
                <span>Change the portion count</span>
              </button>
            )}
          </li>
          <li>
            {/* Discarding keeps the count rather than zeroing it — two portions
              thrown away is the record, and Review can only tell you what you
              waste if the waste stays written down. */}
            <button
              className="pot__action pot__action--bin"
              disabled={gone}
              onClick={() => {
                discard.mutate(pot.id);
                onClose();
              }}
            >
              <span>{gone ? "Thrown out" : "It went in the bin"}</span>
              <span className="muted num">{formatPortions(left)} wasted</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

/* ========================================================================== *
 * 7d — the share composer
 * ========================================================================== */

/** How long a link lasts. "When I say" is a real answer, not an oversight. */
const EXPIRIES: { label: string; days: number | null }[] = [
  { label: "In 7 days", days: 7 },
  { label: "In 30 days", days: 30 },
  { label: "When I say", days: null },
];

export function ShareComposer({ batchId }: { batchId: string }) {
  const ctx = useCalorio();
  const batch = useBatch(ctx, batchId);
  const create = useCreateShare(ctx);
  const patch = useUpdateShare(ctx);
  const revoke = useRevokeShare(ctx);

  /* The switches before there is a link to hang them on. Once one exists it is
     the source of truth — a second copy would be a composer that shows one
     thing and has shared another. */
  const [wantIngredients, setWantIngredients] = useState(true);
  const [wantBatch, setWantBatch] = useState(true);
  const [wantDays, setWantDays] = useState<number | null>(30);
  const [copied, setCopied] = useState(false);

  const shares: BatchShare[] = batch.data?.shares ?? [];
  const link = shares.find((share) => share.live) ?? null;

  const includeIngredients = link ? link.include_ingredients : wantIngredients;
  const includeBatch = link ? link.include_batch : wantBatch;
  const expiryDays = link
    ? link.expires_at
      ? Math.max(1, Math.round((Date.parse(link.expires_at) - Date.now()) / 86_400_000))
      : null
    : wantDays;

  const portions = batch.data?.portions_made ?? 0;
  const remaining = batch.data?.portions_remaining ?? 0;

  const setSwitch = (which: "ingredients" | "pot", on: boolean) => {
    if (!link) {
      if (which === "ingredients") setWantIngredients(on);
      else setWantBatch(on);
      return;
    }
    patch.mutate({
      id: link.id,
      ...(which === "ingredients" ? { include_ingredients: on } : { include_batch: on }),
    });
  };

  const setExpiry = (days: number | null) => {
    if (!link) return setWantDays(days);
    patch.mutate(
      days === null ? { id: link.id, never_expires: true } : { id: link.id, expires_in_days: days },
    );
  };

  const expiryLine =
    expiryDays === null
      ? "No date. It works until you revoke it."
      : `Dies ${new Date(Date.now() + expiryDays * 86_400_000).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
        })}. Opens after that get the closed page.`;

  return (
    <div className="sheet__scroll pot">
      <div className="pot__section">
        <h3 className="sheet__heading">What the link shows</h3>
      </div>

      <Switch
        label="Ingredients and weights"
        on={includeIngredients}
        note={
          includeIngredients
            ? `${batch.data?.ingredients.length ?? 0} lines and their raw weights. On.`
            : "Off. The page shows the name and the per-portion numbers, and nothing about how it was made."
        }
        onChange={(on) => setSwitch("ingredients", on)}
      />
      <Switch
        label="This pot, not just the recipe"
        on={includeBatch}
        note={
          includeBatch
            ? `Cooked ${
                batch.data ? day(batch.data.cooked_date) : "—"
              }, ${formatPortions(remaining)} of ${portions} left. On.`
            : "Off. The link is the recipe only — no cooked date, no count, so it stays true after the pot is gone."
        }
        onChange={(on) => setSwitch("pot", on)}
      />

      <div className="pot__never">
        <strong>Never in the link</strong>
        <p className="muted">
          Your name, your targets, your diary, and anything else you have cooked. The page says what
          a portion is; it does not say whose it is.
        </p>
      </div>

      <div className="pot__section">
        <h3 className="sheet__heading">Stops working</h3>
      </div>
      <div className="pot__dates">
        {EXPIRIES.map((option) => (
          <button
            key={option.label}
            className={`pot__chip${
              (option.days === null ? expiryDays === null : option.days === expiryDays)
                ? " is-on"
                : ""
            }`}
            onClick={() => setExpiry(option.days)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <p className="muted pot__note">{expiryLine}</p>

      <div className="pot__section">
        <h3 className="sheet__heading">The link</h3>
      </div>
      {link ? (
        <div className="pot__link-row">
          <span className="pot__url num">{shareUrl(link.token)}</span>
          <div className="pot__link-actions">
            <button
              className="pot__chip"
              onClick={() => {
                void navigator.clipboard?.writeText(shareUrl(link.token));
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
            {/* Only where the platform has it. A "Send it" that silently does
                nothing on a desktop is worse than one that is not there. */}
            {typeof navigator.share === "function" && (
              <button
                className="pot__chip"
                onClick={() =>
                  void navigator
                    .share({
                      url: shareUrl(link.token),
                      title: batch.data?.recipe_name,
                    })
                    .catch(() => undefined)
                }
              >
                Send it
              </button>
            )}
            <a className="pot__chip" href={shareUrl(link.token)} target="_blank" rel="noreferrer">
              Preview
            </a>
          </div>
        </div>
      ) : (
        <div className="pot__dock pot__dock--inline">
          <button
            className="pot__go"
            disabled={create.isPending}
            onClick={() =>
              create.mutate({
                batchId,
                include_ingredients: wantIngredients,
                include_batch: wantBatch,
                expires_in_days: wantDays,
              })
            }
          >
            {create.isPending ? "Making it…" : "Make the link"}
          </button>
        </div>
      )}

      {shares.length > 0 && (
        <>
          <div className="pot__section">
            <h3 className="sheet__heading">Links you have made</h3>
          </div>
          <ul className="pot__shares">
            {shares.map((share) => (
              <li key={share.id} className="pot__share">
                <div className="pot__share-what">
                  <span className="pot__line-name">{share.batch_name}</span>
                  <span className="pot__line-meta num">
                    {share.revoked_at
                      ? `revoked ${new Date(share.revoked_at).toLocaleDateString(undefined, {
                          day: "numeric",
                          month: "short",
                        })}`
                      : [
                          `opened ${share.opened_count} time${share.opened_count === 1 ? "" : "s"}`,
                          share.expires_at
                            ? `expires ${new Date(share.expires_at).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "short",
                              })}`
                            : "no end date",
                        ].join(" · ")}
                  </span>
                </div>
                {share.live && (
                  <button
                    className="pot__chip"
                    onClick={() => revoke.mutate(share.id)}
                    disabled={revoke.isPending}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * One switch, and what it does said underneath it.
 *
 * The note is the whole control, really. "Ingredients and weights" on its own
 * is a label; "6 lines and their raw weights. On." is a description of the page
 * somebody else is about to read, which is the thing being decided.
 */
function Switch({
  label,
  on,
  note,
  onChange,
}: {
  label: string;
  on: boolean;
  note: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="pot__switch">
      <button
        role="switch"
        aria-checked={on}
        className={`pot__toggle${on ? " is-on" : ""}`}
        onClick={() => onChange(!on)}
      >
        <span className="pot__toggle-label">{label}</span>
        <span className="pot__toggle-track" aria-hidden="true">
          <span className="pot__toggle-knob" />
        </span>
      </button>
      <p className="muted pot__note">{note}</p>
    </div>
  );
}
